// ─────────────────────────────────────────────────────────────────────────────
//  wallet.js — Intégration Wallet Standard Solana
//
//  Pourquoi le Wallet Standard ?
//  L'ancienne méthode (V1) vérifiait manuellement : window.phantom?.solana,
//  window.solflare, window.backpack... C'était fragile : si un nouveau wallet
//  sortait, il fallait modifier le code.
//
//  Le Wallet Standard inverse le principe : ce sont les WALLETS qui s'annoncent
//  à l'application. Ton code n'a pas besoin de savoir si Phantom existe —
//  Phantom dit lui-même "je suis là, voici mes fonctionnalités".
//
//  Analogie : en V1 tu frappais à toutes les portes du quartier pour trouver
//  un taxi. Avec le Wallet Standard, les taxis klaxonnent d'eux-mêmes.
//
//  Standard utilisé : https://github.com/wallet-standard/wallet-standard
//  Compatible : Phantom, Solflare, Backpack, Coinbase, Brave, et tout futur wallet.
// ─────────────────────────────────────────────────────────────────────────────

import { getWallets } from '@wallet-standard/app';
import { store } from './store';
import { config } from './config.js';

// ── Détection des wallets disponibles ────────────────────────────────────────

// getWallets() retourne deux outils :
//   get() → tableau des wallets déjà enregistrés au moment de l'appel
//   on()  → abonnement pour les wallets qui s'enregistrent plus tard
//           (ex: l'extension Phantom qui finit de s'initialiser)
const { get: getRegisteredWallets, on: onWalletRegister } = getWallets();

// Liste réactive des wallets Solana disponibles
let _availableWallets = [];

// Un wallet Solana = un wallet qui supporte au moins signAndSendTransaction
// (la feature de base pour envoyer des transactions sur Solana)
function isSolanaWallet(wallet) {
  return (
    'solana:signAndSendTransaction' in wallet.features ||
    'solana:signTransaction' in wallet.features
  );
}

function refreshAvailableWallets() {
  _availableWallets = getRegisteredWallets().filter(isSolanaWallet);
  // Notifie le store — la nav peut ainsi afficher la liste à jour
  store.set('availableWallets', _availableWallets);
}

// Premier scan au chargement
refreshAvailableWallets();

// Écoute les wallets qui s'enregistrent après le chargement initial
// (fréquent : les extensions browser s'initialisent quelques ms après le script)
onWalletRegister('register', () => refreshAvailableWallets());

// ── État interne ──────────────────────────────────────────────────────────────

let _activeWallet  = null;  // Objet wallet Wallet Standard actif
let _activeAccount = null;  // Compte sélectionné dans ce wallet

// ── Connexion ─────────────────────────────────────────────────────────────────
//
// Appelé quand l'utilisateur clique sur "Connecter" et choisit un wallet.
// Le wallet ouvre son popup de confirmation — l'utilisateur approuve ou refuse.

export async function connectWallet(wallet) {
  // Vérifie que ce wallet supporte la connexion standard
  const connectFeature = wallet.features['standard:connect'];
  if (!connectFeature) {
    throw new Error(`${wallet.name} ne supporte pas standard:connect`);
  }

  // Met à jour l'état de chargement (pour afficher un spinner)
  store.merge('loading', { wallet: true });

  try {
    // Ouvre le popup de connexion du wallet
    const { accounts } = await connectFeature.connect();

    if (!accounts.length) {
      throw new Error('Aucun compte retourné par le wallet');
    }

    // On prend le premier compte (le compte actif dans le wallet)
    _activeWallet  = wallet;
    _activeAccount = accounts[0];

    // Met à jour l'état global — toutes les pages qui écoutent 'change:wallet' se rafraîchissent
    store.set('wallet', {
      connected:  true,
      publicKey:  _activeAccount.address,  // adresse base58 (string)
      name:       wallet.name,
      icon:       wallet.icon,             // data URL de l'icône
    });

    // Persist la préférence wallet (pour auto-reconnexion)
    localStorage.setItem('pros_last_wallet', wallet.name);

    // Écoute les changements du wallet (déconnexion externe, changement de compte)
    _listenToWalletEvents(wallet);

    return _activeAccount;

  } finally {
    store.merge('loading', { wallet: false });
  }
}

// ── Déconnexion ───────────────────────────────────────────────────────────────

export async function disconnectWallet() {
  // Certains wallets ont une méthode disconnect explicite
  const disconnectFeature = _activeWallet?.features['standard:disconnect'];
  if (disconnectFeature) {
    try { await disconnectFeature.disconnect(); } catch (_) { /* ignore */ }
  }

  _activeWallet  = null;
  _activeAccount = null;
  localStorage.removeItem('pros_last_wallet');

  // Remet l'état wallet à zéro
  store.set('wallet', {
    connected:  false,
    publicKey:  null,
    name:       null,
    icon:       null,
  });
}

// ── Écoute des changements du wallet ─────────────────────────────────────────
//
// Quand l'utilisateur change de compte dans Phantom ou déconnecte depuis
// l'extension, on met à jour l'état sans attendre qu'il clique sur quelque chose.

function _listenToWalletEvents(wallet) {
  const eventsFeature = wallet.features['standard:events'];
  if (!eventsFeature) return;

  eventsFeature.on('change', ({ accounts }) => {
    if (!accounts || accounts.length === 0) {
      // Le wallet a été déconnecté depuis l'extension
      disconnectWallet();
    } else if (accounts[0].address !== _activeAccount?.address) {
      // L'utilisateur a changé de compte dans son wallet
      _activeAccount = accounts[0];
      store.merge('wallet', { publicKey: accounts[0].address });
    }
  });
}

// ── Tentative de reconnexion automatique ──────────────────────────────────────
//
// Si l'utilisateur a connecté un wallet lors d'une session précédente,
// on essaie de se reconnecter silencieusement (sans popup).
// Certains wallets supportent le "silent connect" — pas tous.
// Si ça échoue, on ignore l'erreur sans déranger l'utilisateur.

export async function tryAutoReconnect() {
  const lastWalletName = localStorage.getItem('pros_last_wallet');
  if (!lastWalletName) return;

  // Petit délai pour laisser le temps aux wallets de s'enregistrer
  await new Promise(resolve => setTimeout(resolve, 300));

  const wallet = _availableWallets.find(w => w.name === lastWalletName);
  if (!wallet) return;

  try {
    const connectFeature = wallet.features['standard:connect'];
    // silent: true → pas de popup, échoue silencieusement si non autorisé
    const { accounts } = await connectFeature.connect({ silent: true });
    if (accounts.length) {
      await connectWallet(wallet);
    }
  } catch (_) {
    // Pas de reconnexion silencieuse possible — normal, on ne fait rien
  }
}

// ── Helpers publics ───────────────────────────────────────────────────────────

// Retourne la liste des wallets Solana installés dans le browser
export function getAvailableWallets() {
  return _availableWallets;
}

// Retourne le wallet et le compte actifs
export function getActiveWallet() {
  return { wallet: _activeWallet, account: _activeAccount };
}

// Vérifie si le wallet connecté est l'admin (compare avec config.adminWallet)
export function isAdmin() {
  const { publicKey } = store.get('wallet');
  return publicKey === config.adminWallet;
}

// ── Adapter Anchor ────────────────────────────────────────────────────────────
//
// @coral-xyz/anchor attend un objet wallet avec une interface spécifique
// (publicKey, signTransaction, signAllTransactions).
// Cet adapter fait le pont entre le Wallet Standard et Anchor.
// Sera utilisé quand on créera l'AnchorProvider pour les instructions on-chain.
//
// Note : @solana/web3.js v1 utilise PublicKey (objet), pas une string.
// La conversion se fait ici.

export function getAnchorWalletAdapter() {
  if (!_activeWallet || !_activeAccount) return null;

  // Import lazy de PublicKey pour ne pas charger web3.js si pas nécessaire
  return {
    get publicKey() {
      // Importé dynamiquement depuis @coral-xyz/anchor (qui inclut web3.js v1)
      const { PublicKey } = window.__web3js__ ?? require('@solana/web3.js');
      return new PublicKey(_activeAccount.address);
    },

    async signTransaction(transaction) {
      const feature = _activeWallet.features['solana:signTransaction'];
      if (!feature) throw new Error('Ce wallet ne supporte pas signTransaction');
      const chain = `solana:${config.cluster}`;
      const { signedTransactions } = await feature.signTransaction({
        transactions: [{ transaction, account: _activeAccount, chain }],
      });
      return signedTransactions[0];
    },

    async signAllTransactions(transactions) {
      const feature = _activeWallet.features['solana:signTransaction'];
      if (!feature) throw new Error('Ce wallet ne supporte pas signTransaction');
      const chain = `solana:${config.cluster}`;
      const { signedTransactions } = await feature.signTransaction({
        transactions: transactions.map(tx => ({
          transaction: tx,
          account: _activeAccount,
          chain,
        })),
      });
      return signedTransactions;
    },
  };
}
